import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Stack,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  RefreshOutlined,
  EditOutlined,
  DeleteOutlineRounded,
  PeopleAltOutlined,
  SupportAgentOutlined,
  AdminPanelSettingsOutlined,
  BusinessOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import UserEditDialog from "../components/UserEditDialog";
import { textPrimary, textSecondary } from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PAGE_SIZE = 13;

const TYPES = [
  {
    key: "destek",
    label: "Destek Personelleri",
    icon: SupportAgentOutlined,
    color: "#E67E22",
  },
  {
    key: "admin",
    label: "Yöneticiler",
    icon: AdminPanelSettingsOutlined,
    color: "#C62828",
  },
  {
    key: "user",
    label: "Müşteri Kullanıcıları",
    icon: PeopleAltOutlined,
    color: "#1976D2",
  },
  {
    key: "rootUser",
    label: "Admin Müşteriler",
    icon: BusinessOutlined,
    color: "#2E7D32",
  },
];

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

// function formatDate(iso) {
//   if (!iso) return "-";
//   try {
//     const d = new Date(iso);
//     if (isNaN(d.getTime())) return iso;
//     const p = (n) => String(n).padStart(2, "0");
//     return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
//   } catch {
//     return iso;
//   }
// }
// Timezone bilgisi yoksa UTC kabul ederek Date objesi döner
function parseDate(iso) {
  if (!iso) return null;
  try {
    let str = String(iso).trim();
    // Z / +03:00 / GMT / UTC hepsini yakalar
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$|\b(GMT|UTC)\b/i.test(str);
    if (!hasTz) str = str.replace(" ", "T") + "Z";
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

// GMT'siz, gg/aa/yyyy ss:dd
function formatDate(iso) {
  const d = parseDate(iso);
  if (!d) return iso || "-";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const [activeType, setActiveType] = useState("destek");
  const [selectedSunucu, setSelectedSunucu] = useState("");
  const [sunucuKoduList, setSunucuKoduList] = useState([]);

  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const needsSunucu = (t) => t === "user" || t === "rootUser";

  /* ─────────── Sunucu kodlarını yükle ─────────── */
  useEffect(() => {
    (async () => {
      try {
        const companies = await adminService.getCompanies();
        setSunucuKoduList(companies.map((c) => c.sunucu_kodu).filter(Boolean));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  /* ─────────── Kullanıcıları yükle ─────────── */
  const loadData = async (
    silent = false,
    type = activeType,
    sunucu = selectedSunucu,
  ) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await adminService.getUsersByType({
        type,
        sunucuKodu: needsSunucu(type) ? sunucu || "" : "",
        force: true,
      });
      const mapped = data.map((u) => ({
        id: String(u.id),
        username: u.username ?? "-",
        phone: u.phone ?? "",
        email: u.email ?? "",
        sunucuKodu: u.sunucu_kodu ?? "",
        type: u.type ?? "",
        createdAt: u.created_at,
      }));
      setRows(mapped);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError("Kullanıcılar yüklenemedi.");
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false, activeType, selectedSunucu);
    // eslint-disable-next-line
  }, [activeType, selectedSunucu]);

  /* ─────────── Sayfalama ─────────── */
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  /* ─────────── Silme ─────────── */
  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.username}" silinsin mi?`)) return;
    try {
      await adminService.deleteUser(row.id);
      setSnack({ open: true, msg: "Kullanıcı silindi.", severity: "success" });
      await loadData(true);
    } catch (e) {
      console.error(e);
      setSnack({
        open: true,
        msg: "Silme başarısız.",
        severity: "error",
      });
    }
  };

  /* ─────────── Tab config ─────────── */
  const activeConfig = TYPES.find((t) => t.key === activeType);
  const showSunucuCol = needsSunucu(activeType);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          flexWrap: "wrap",
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={800}
          color="#fff"
          sx={{ fontSize: 18 }}
        >
          Kullanıcılar
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.4,
            borderRadius: 5,
            backgroundColor: "rgba(255,255,255,0.14)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            border: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          {rows.length} kayıt
        </Box>

        <Stack direction="row" spacing={0.75} sx={{ ml: "auto" }}>
          <Tooltip title="Yenile">
            <span>
              <IconButton
                size="small"
                onClick={() => loadData(true)}
                disabled={loading || refreshing}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
                }}
              >
                <RefreshOutlined
                  sx={{
                    fontSize: 18,
                    animation:
                      loading || refreshing
                        ? "spin 1.2s linear infinite"
                        : "none",
                    "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Kullanıcı Tipi Sekmeleri */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: "wrap", gap: 0.75, flexShrink: 0 }}
      >
        {TYPES.map((t) => {
          const Icon = t.icon;
          const active = activeType === t.key;
          return (
            <Button
              key={t.key}
              onClick={() => {
                setActiveType(t.key);
                setPage(1);
                if (!needsSunucu(t.key)) setSelectedSunucu("");
              }}
              startIcon={<Icon />}
              sx={{
                textTransform: "none",
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: 12.5,
                px: 1.75,
                py: 0.9,
                color: active ? "#fff" : "rgba(255,255,255,0.85)",
                backgroundColor: active ? t.color : "rgba(255,255,255,0.10)",
                border: "1px solid",
                borderColor: active ? t.color : "rgba(255,255,255,0.20)",
                transition: "transform 0.2s ease, background 0.2s ease",
                "&:hover": {
                  backgroundColor: active ? t.color : "rgba(255,255,255,0.18)",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0) scale(0.98)" },
              }}
            >
              {t.label}
            </Button>
          );
        })}
      </Stack>

      {/* Sunucu Kodu (sadece user/rootUser) */}
      {showSunucuCol && (
        <Autocomplete
          size="small"
          options={sunucuKoduList}
          value={selectedSunucu || null}
          onChange={(_, v) => {
            setSelectedSunucu(v || "");
            setPage(1);
          }}
          filterOptions={(opts, { inputValue }) =>
            opts.filter((o) => normalize(o).includes(normalize(inputValue)))
          }
          noOptionsText="Sunucu bulunamadı"
          sx={{ maxWidth: 400, flexShrink: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Sunucu kodu ile filtrele..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255,255,255,0.94)",
                  borderRadius: 2,
                },
              }}
            />
          )}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 2.5,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "rgba(255,255,255,0.98)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
              border: "1px solid rgba(255,255,255,0.30)",
              animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(15,50,65,0.20)",
                  borderRadius: 3,
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        background:
                          "linear-gradient(180deg, #F1F5F9 0%, #E2E8F0 100%)",
                        color: "#334155",
                        fontWeight: 800,
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        py: 1,
                        px: 1.5,
                        borderBottom: "1px solid #CBD5E1",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell>#</TableCell>
                    <TableCell>Kullanıcı Adı</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell>Email</TableCell>
                    {showSunucuCol && <TableCell>Sunucu Kodu</TableCell>}
                    <TableCell>Tarih</TableCell>
                    <TableCell align="right">İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={showSunucuCol ? 7 : 6}
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: textSecondary,
                          fontSize: 13,
                        }}
                      >
                        Kayıt bulunamadı
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((r, i) => (
                      <TableRow
                        key={r.id}
                        sx={{
                          backgroundColor: i % 2 ? "#F8FAFC" : "#fff",
                          "&:hover": {
                            backgroundColor: "rgba(4,87,129,0.06)",
                          },
                          transition: "background-color 0.2s ease",
                          "& td": {
                            py: 0.75,
                            px: 1.5,
                            fontSize: 12.5,
                            color: "#334155",
                            borderBottom: "1px solid #F1F5F9",
                          },
                        }}
                      >
                        <TableCell sx={{ color: textSecondary }}>
                          {r.id}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: textPrimary }}>
                          {r.username}
                        </TableCell>
                        <TableCell>{r.phone || "-"}</TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {r.email || "-"}
                        </TableCell>
                        {showSunucuCol && (
                          <TableCell>{r.sunucuKodu || "-"}</TableCell>
                        )}
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            color: textSecondary,
                            fontSize: 11.5,
                          }}
                        >
                          {formatDate(r.createdAt)}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setEditDialog({ open: true, user: r })
                              }
                              sx={{
                                color: "#045781",
                                "&:hover": {
                                  backgroundColor: "rgba(4,87,129,0.10)",
                                },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(r)}
                              sx={{
                                color: "#B91C1C",
                                "&:hover": {
                                  backgroundColor: "rgba(185,28,28,0.10)",
                                },
                              }}
                            >
                              <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {rows.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                px: 0.5,
                animation: `${fadeInUp} 0.4s ease-out 0.2s backwards`,
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                {Math.min((safePage - 1) * PAGE_SIZE + 1, rows.length)}–
                {Math.min(safePage * PAGE_SIZE, rows.length)} / {rows.length}{" "}
                kayıt
              </Typography>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, p) => setPage(p)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#fff",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      fontWeight: 800,
                    },
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <UserEditDialog
        open={editDialog.open}
        user={editDialog.user}
        sunucuKoduList={sunucuKoduList}
        onClose={() => setEditDialog({ open: false, user: null })}
        onSaved={() => {
          setSnack({
            open: true,
            msg: "Kullanıcı güncellendi.",
            severity: "success",
          });
          loadData(true);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
